import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { IFilterModel } from '@common/types';
import { IEnhancedRow } from '@components/table/types';
import ListingTableWithCellInputs from '@components/table/with-cell-inputs/ListingTableWithCellInputs';

import { getAmendmentInsuredDetailsHeaders } from '../constants';
import {
  FormValuesCancellation,
  IAmendmentCancellationInsuredDetails,
  IInsuredsDetails
} from '../types';
import { getInsuredPolicyMedicalRowFieldErrors } from '../utils/getInsuredPolicyMedicalRowFieldErrors';
import { parseVarianceInputValue } from '../utils/parseVarianceInputValue';

import { useFormikContext } from 'formik';

type VarianceField =
  | 'netPremiumVariance'
  | 'chargesVariance'
  | 'tpaFeesVariance';

type InitialVariance = Record<string, Record<VarianceField, number>>;

const VARIANCE_FIELDS: VarianceField[] = [
  'netPremiumVariance',
  'chargesVariance',
  'tpaFeesVariance'
];

const VALUES_CHANGED_MESSAGE = 'Values have been changed.';

type Props = {
  filterValues: IFilterModel;
  disabled: boolean;
  tableData: IInsuredsDetails[] | IAmendmentCancellationInsuredDetails[];
  layoutName: string;
  handlePageChange: (filter: IFilterModel) => Promise<void>;
  policyCurrency: string;
};

const CancelalationInsuredDetailsWidgetAtom: React.FC<Props> = ({
  filterValues,
  layoutName,
  disabled,
  handlePageChange,
  policyCurrency,
  tableData
}) => {
  const { values, setFieldValue, errors } =
    useFormikContext<FormValuesCancellation>();
  const initialVarianceRef = useRef<InitialVariance>({});
  const baselineCapturedIdsRef = useRef('');

  const captureInitialVariances = useCallback(
    (items: (IInsuredsDetails | IAmendmentCancellationInsuredDetails)[]) => {
      const next: InitialVariance = {};
      items.forEach((item) => {
        next[item.id] = {
          netPremiumVariance: Number(item.netPremiumVariance ?? 0),
          chargesVariance: Number(item.chargesVariance ?? 0),
          tpaFeesVariance: Number(item.tpaFeesVariance ?? 0)
        };
      });
      initialVarianceRef.current = next;
    },
    []
  );

  useEffect(() => {
    const idsKey = tableData.map((item) => item.id).join('|');
    if (baselineCapturedIdsRef.current === idsKey) {
      return;
    }

    baselineCapturedIdsRef.current = idsKey;
    captureInitialVariances(tableData);
  }, [captureInitialVariances, tableData]);

  const displayPagedItems = useMemo(
    () =>
      tableData.map((item, index) => ({
        ...item,
        ...(values.insuredPolicyMedicals?.[index] ?? {}),
        fieldValidationErrors: getInsuredPolicyMedicalRowFieldErrors(
          errors,
          index
        )
      })),
    [errors, tableData, values.insuredPolicyMedicals]
  );

  const dynamicRowMessage = (row: IEnhancedRow): string[] => {
    const initial = initialVarianceRef.current[row.columns.id];
    if (!initial) {
      return [];
    }

    for (const field of VARIANCE_FIELDS) {
      if (Number(row.columns[field]) !== Number(initial[field])) {
        return [VALUES_CHANGED_MESSAGE];
      }
    }

    return [];
  };

  const handleTableValueChange = (
    row: any,
    columnName: string,
    value: unknown,
    rowId: string,
    index: any
  ) => {
    if (value === '' || value === null || value === undefined) {
      setFieldValue(
        `insuredPolicyMedicals[${index}].${columnName}`,
        undefined,
        true
      );

      return;
    }

    const parsedValue = parseVarianceInputValue(value);
    if (parsedValue === null) {
      return;
    }

    if (!Number.isNaN(parsedValue)) {
      setFieldValue(
        `insuredPolicyMedicals[${index}].${columnName}`,
        parsedValue,
        true
      );
    }
  };

  const validateTableAmountReceived = (
    index: any,
    columnName: string,
    value: unknown,
    row: any
  ) => {
    if (columnName) {
      if (value === '' || value === null || value === undefined) {
        setFieldValue(
          `insuredPolicyMedicals[${index}].${columnName}`,
          undefined,
          true
        );
      } else {
        const parsedValue = parseVarianceInputValue(value, { finalize: true });
        if (parsedValue !== null && !Number.isNaN(parsedValue)) {
          setFieldValue(
            `insuredPolicyMedicals[${index}].${columnName}`,
            parsedValue,
            true
          );
        }
      }
    }

    setFieldValue(
      `insuredPolicyMedicals[${index}].grossPremiumVariance`,
      (row.netPremiumVariance ?? 0) +
        (row.chargesVariance ?? 0) +
        (row.tpaFeesVariance ?? 0),
      true
    );
    setFieldValue(
      `insuredPolicyMedicals[${index}].coverProportionalStamp`,
      row.proportionalStampRate *
        (values.insuredPolicyMedicals[index].grossPremiumVariance || 0),
      true
    );
    setFieldValue(
      `insuredPolicyMedicals[${index}].coverMunicipalityTax`,
      row.municipalityTaxRate *
        (values.insuredPolicyMedicals[index].grossPremiumVariance || 0),
      true
    );
    setFieldValue(
      `insuredPolicyMedicals[${index}].totalPremiumVariance`,
      values.insuredPolicyMedicals[index].grossPremiumVariance +
        (values.insuredPolicyMedicals[index].coverProportionalStamp || 0) +
        (values.insuredPolicyMedicals[index].coverMunicipalityTax || 0),
      true
    );
  };

  return (
    <ListingTableWithCellInputs
      name="insuredsDetails"
      inlineTitle="Insureds Details"
      headers={getAmendmentInsuredDetailsHeaders(policyCurrency, disabled)}
      data={{
        pagedItems: displayPagedItems,
        pageNumber: filterValues.pagination?.pageNumber || 1,
        pageSize: filterValues.pagination?.pageSize || 5,
        totalCount: values.totalCount ?? 0
      }}
      handleCellValueChanged={(index, columnName, value, rowId, row) => {
        handleTableValueChange(row, columnName, value, rowId, index);
      }}
      handleUpdate={(index, columnName, value, row) => {
        validateTableAmountReceived(index, columnName, value, row);
      }}
      inline
      scrollable
      dynamicRowMessage={dynamicRowMessage}
      disableSelection
      defaultOrderByColumn="policyPerson.relation"
      orderByAscendingByDefault
      rowsPerPageOptions={[5, 10, 25]}
      onPageChange={(page, pageSize) => {
        const newFilterModel: IFilterModel = {
          ...filterValues,
          pagination: {
            pageNumber: page,
            pageSize: pageSize
          },
          orderBy: filterValues.orderBy,
          descending: filterValues.descending,
          searchKey: filterValues.searchKey
        };

        return handlePageChange(newFilterModel);
      }}
      onSortChange={(order, orderBy) => {
        const incomingOrderBy = order?.trim();

        const newFilterModel: IFilterModel = {
          ...filterValues,
          pagination: {
            pageNumber: 1,
            pageSize: 5
          },
          orderBy: incomingOrderBy || filterValues.orderBy,
          descending: orderBy,
          searchKey: filterValues.searchKey
        };

        return handlePageChange(newFilterModel);
      }}
    />
  );
};

export { CancelalationInsuredDetailsWidgetAtom };
